import React, { useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { useAddFormDateChangeRequestMutation, useGetFormDateChangeRequestListQuery } from '../../../../../api';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import toastService from '../../../../../utils/toastService';
import { DATE_CHANGE_STATUS } from '../../../../../utils/formDateChangeConstants';

/**
 * Kabul edilmiş VAF / PSI / CVRF kayıtlarında tarih alanları normal Update ile
 * değiştirilemiyor (bkz. docs/FormDateChangeRequest_FE_Integration.md). Bu bileşen
 * o kayıtlarda ilgili tarih alanının yerine geçer: salt-okunur tarih gösterimi +
 * "Tarihi Değiştir" butonu + talep dialogu.
 *
 * Kullanım: sadece kaydın kendi statüsü "kabul edilmiş" iken (VAF/CVRF Approved,
 * PSI SendToKarsan) render edilmeli; diğer durumlarda ilgili formun kendi
 * (düzenlenebilir) Calendar alanı kullanılmaya devam eder.
 */
const DateChangeRequestField = ({ formType, dateField, formId, currentDate, label, onSuccess, disabled = false }) => {
    const { th } = useLanguage();
    const { hasWarrantyTeamRole, hasWarrantyManagerRole, hasAdminRole } = usePermissions();
    const canRequestDateChange = hasWarrantyTeamRole() || hasWarrantyManagerRole() || hasAdminRole();
    const isDirectUpdate = hasWarrantyManagerRole() || hasAdminRole();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [newDate, setNewDate] = useState(null);
    const [description, setDescription] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const numericFormId = formId ? Number(formId) : null;

    const { data: pendingData } = useGetFormDateChangeRequestListQuery({ formType, formId: numericFormId, dateField, status: DATE_CHANGE_STATUS.SENT_FOR_APPROVAL, pageNumber: 1, pageSize: 1 }, { skip: !numericFormId || !canRequestDateChange });
    const hasPendingRequest = (pendingData?.totalCount ?? 0) > 0;

    const [addRequest, { isLoading: isSubmitting }] = useAddFormDateChangeRequestMutation();

    const openDialog = () => {
        setNewDate(null);
        setDescription('');
        setSubmitted(false);
        setDialogVisible(true);
    };

    const closeDialog = () => setDialogVisible(false);

    const handleSubmit = async () => {
        setSubmitted(true);
        if (!newDate || !numericFormId) return;

        try {
            await addRequest({
                formType,
                formId: numericFormId,
                dateField,
                newDate: newDate.toISOString(),
                description: description || undefined
            }).unwrap();

            toastService.success(isDirectUpdate ? th('formDateChangeRequest.toast.submitDirectSuccess') : th('formDateChangeRequest.toast.submitSuccess'));
            closeDialog();
            onSuccess?.();
        } catch (error) {
            toastService.error(error?.data?.errorMessage || th('formDateChangeRequest.toast.submitError'));
        }
    };

    return (
        <>
            <div className="flex align-items-center gap-2">
                <Calendar value={currentDate} disabled showIcon dateFormat="dd/mm/yy" className="w-full" />
                {canRequestDateChange && (
                    <Button
                        type="button"
                        icon="pi pi-pencil"
                        label={th('formDateChangeRequest.dialog.changeDate')}
                        className="p-button-outlined p-button-sm white-space-nowrap"
                        onClick={openDialog}
                        disabled={disabled || hasPendingRequest || !numericFormId}
                    />
                )}
            </div>
            {canRequestDateChange && hasPendingRequest && <small className="text-orange-600 block mt-1">{th('formDateChangeRequest.dialog.pendingRequestWarning')}</small>}

            <Dialog visible={dialogVisible} onHide={closeDialog} modal style={{ width: '450px' }} header={label || th('formDateChangeRequest.dialog.changeDateDialogTitle')}>
                <div className="field mb-3">
                    <label className="font-bold block mb-2">{th('formDateChangeRequest.dialog.newDateLabel')} *</label>
                    <Calendar value={newDate} onChange={(e) => setNewDate(e.value)} showIcon dateFormat="dd/mm/yy" className={`w-full ${submitted && !newDate ? 'p-invalid' : ''}`} />
                    {submitted && !newDate && <small className="p-error">{th('formDateChangeRequest.dialog.newDateRequired')}</small>}
                </div>
                <div className="field mb-3">
                    <label className="font-bold block mb-2">{th('formDateChangeRequest.dialog.descriptionLabel')}</label>
                    <InputTextarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500} className="w-full" placeholder={th('formDateChangeRequest.dialog.descriptionPlaceholder')} />
                </div>
                {!isDirectUpdate && <Message severity="info" text={th('formDateChangeRequest.dialog.submitForApproval')} className="w-full mb-3" />}
                <div className="flex justify-content-end gap-2">
                    <Button label={th('formDateChangeRequest.dialog.cancel')} icon="pi pi-times" className="p-button-text" onClick={closeDialog} disabled={isSubmitting} />
                    <Button label={isDirectUpdate ? th('formDateChangeRequest.dialog.submitDirectUpdate') : th('formDateChangeRequest.dialog.submitForApproval')} icon="pi pi-check" onClick={handleSubmit} loading={isSubmitting} />
                </div>
            </Dialog>
        </>
    );
};

export default DateChangeRequestField;
